import * as _ from 'underscore';
import { Keys } from '../helpers/Keys';
import { Module } from '../Module';

// A sentinel value to mean mixed text field values
export let TextBoxMixedValues = '———';

interface ITextBoxDirective {
  name: string;
  template: string;
}

const directives: ITextBoxDirective[] = [
  { name: 'tbTextBox',       template: 'textBox.html'     },
  { name: 'tbPasswordBox',   template: 'passwordBox.html' },
  { name: 'tbNumberBox',     template: 'numberBox.html' }
];

export interface ITextBoxScope extends ng.IScope {
  disabled: boolean;
  required: boolean;
  model: string;
  options: angular.INgModelOptions;
  maxLength: string;
  onEnter: () => any;
  focus: string;
  clearable: boolean;
  onClear: () => any;
  onPaste: () => any;
  inputTestId: string;
  triggerEnter: () => void;
  textValue: string;
  validation: string;
  min: number;
  max: number;
  match: string;
  syncCommitModel: () => void;
  clearAndFocus: () => void;
  clearValue: () => void;
  modelCtrl: ng.INgModelController;
  ariaExpanded: any;
  ariaControls: any;
  ariaAutocomplete: any;
  ariaHaspopup: any;
  ariaActivedescendant: any;
}

/**
 * A wrapper around input(type="text") or input(type="password"). Will put given tb-test-id
 * onto the input element for easier testing.
 *
 * Usage:
 *
 *  tb:text-box(tb-text-box-model="model", tb-test-id="testId")
 *
 * Attributes:
 *
 * tb-text-box-model="model":     Model to bind to
 * tb-text-box-name="n":          Used for form validation.
 * tb-text-box-disabled="bool":   See ng-disabled directive
 * tb-text-box-required="bool":   See ng-required directive
 * tb-text-box-keydown="keydown($event)": See ng-keydown directive
 * tb-text-box-enter="enter()":   See tb-enter directive
 * tb-text-box-auto-select="bool":see tb-auto-select directive
 * tb-text-box-max-length="32":   Adds maxlength attribute to input element
 * tb-text-box-focus:             If present, puts focus on element when linked
 * tb-text-box-clearable="bool":  Has clear button when true
 * tb-text-box-clear="clear()":   Called when clear button is pressed
 * tb-text-box-paste="p()":       Called when content is pasted into the element
 * tb-text-box-model-options="{'updateOn': 'enter clear'}":
 *   Modeled after Angular 1.3 ngModelOptions
 */
_.each(directives, (directive: ITextBoxDirective) => {
  Module.instance().directive(directive.name, <any[]>['$timeout', '$compile',
    ($timeout: ng.ITimeoutService, $compile: ng.ICompileService) => {
      return {
        restrict: 'E',
        replace: true,
        templateUrl: directive.template,
        scope: {
          disabled: '=tbTextBoxDisabled',
          required: '=tbTextBoxRequired',
          model: '=tbTextBoxModel',
          options: '=?tbTextBoxModelOptions',
          maxLength: '@tbTextBoxMaxLength',
          onEnter: '&tbTextBoxEnter',
          focus: '=tbTextBoxFocus',
          clearable: '=tbTextBoxClearable',
          onClear: '&tbTextBoxClear',
          onPaste: '&tbTextBoxPaste',
          autoSelect: '=tbTextBoxAutoSelect',
          keydown: '&tbTextBoxKeydown',
          validation: '@tbTextBoxValidation',
          min: '=tbTextBoxValidationMin', // min and max are used by validation directives
          max: '=tbTextBoxValidationMax',
          match: '=tbMatchData', // used by validation directives
          placeholder: '=tbTextBoxPlaceholder',
          clearValue: '=?tbTextBoxClearValue',
          tabIndex: '@tbTextBoxTabindex',
          ariaActivedescendant: '=tbAriaActivedescendant',
          ariaExpanded: '=tbAriaExpanded',
          ariaControls: '=tbAriaControls',
          ariaAutocomplete:'@tbAriaAutocomplete',
          ariaHaspopup: '@tbAriaHaspopup',
          ariaLabelledBy: '@tbTextBoxAriaLabelledBy'
        },

        link: (scope: ITextBoxScope, element: JQuery, attrs: ng.IAttributes) => {
          if(_.isUndefined(scope.model)) scope.model = '';  // Ensure the model is initialized to an empty string

          let inputElement = element.find('.tb-text-box-input');

          // TODO: make tbTextBox support ng-model on itself instead of using tb-text-box-model
          scope.modelCtrl = angular.element(inputElement).controller('ngModel');

          // This is a workaround for Angular form validation not respecting interpolated input names
          // Angularjs issue#1404
          if (attrs['tbTextBoxName']){
            inputElement.prop('name', attrs['tbTextBoxName']);
          }

          if (scope.validation) {
            const inputElementClone = inputElement.clone();
            _.each(scope.validation.split(' '), (validatorName: string) => {
              inputElementClone.attr(validatorName, '');
            });
            inputElement.replaceWith(inputElementClone);
            $compile(inputElementClone)(scope);
            inputElement = inputElementClone;
          }

          scope.clearValue = () => scope.textValue = '';

          scope.$watch('model', () => {
            scope.textValue = scope.model;

            // Mixed-values handling
            if (scope.model === TextBoxMixedValues) {
              addMixedValueHandlers();
            } else {
              removeMixedValueHandlers();
            }
          });

          if (attrs['tbTestId']) {
            scope.inputTestId = attrs['tbTestId'] + '-input';
          }

          if (scope.maxLength !== undefined) {
            inputElement.attr('maxlength', scope.maxLength);
          }

          if (scope.ariaExpanded !== undefined) {
            inputElement.attr('aria-expanded', attrs['tbAriaExpanded']);
          }

          if (scope.ariaAutocomplete !== undefined) {
            inputElement.attr('aria-autocomplete', attrs['tbAriaAutocomplete']);
          }

          // The timeout is necessary because the element may not be
          // rendered yet. $timeout gives it time to render
          const focus = () => $timeout(() => inputElement.focus());

          scope.clearAndFocus = () => {
            scope.textValue = '';
            if (scope.onClear) {
              scope.onClear();
            }
            inputElement.trigger('clear');
            focus();
          };

          scope.triggerEnter = () => {
            inputElement.trigger('enter');
          };

          const syncModelToText = () => scope.model = scope.textValue;

          scope.$watch('textValue', syncModelToText);

          inputElement.on('focus', () => {
            inputElement.parent().addClass('tb-border-with-focus');
            element.addClass('tb-focus');
          });
          inputElement.on('blur', () => {
            inputElement.parent().removeClass('tb-border-with-focus');
            element.removeClass('tb-focus');
          });

          // Special handling for mixed values
          const addMixedValueHandlers = _.once(() => {

            inputElement.addClass('tb-text-box-mixed');

            // ... Select all text when focused
            inputElement.on('focus.mixed', () => {
              inputElement.select();
            });

            // ... Try to prevent deselection
            inputElement.on('keydown.mixed', ($event: JQuery.Event) => {
              switch ($event.which) {
                case Keys.ArrowDown:
                case Keys.ArrowUp:
                case Keys.ArrowLeft:
                case Keys.ArrowRight:
                case Keys.Home:
                case Keys.End:
                  $event.preventDefault();
              }
            });
            // ... This prevents deselection by not allowing the user to click to set the cursor position
            inputElement.on('mouseup.mixed', ($event: JQuery.Event) => {
              $event.preventDefault();
            });
          });

          const removeMixedValueHandlers = () => {
            inputElement.removeClass('tb-text-box-mixed');
            inputElement.off('.mixed');
          };

        }
      };
    }
  ]);
});
